/**
 * StartConversationDialog - Modal para iniciar atendimento com um contato
 */

import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProviderCapability, ProviderId } from '@troia-v3/types';
import type { ContactIdentifiers } from '@troia-v3/types';
import { Button, Input, Select } from '@/shared/components/ui';
import { Modal, ModalHeader, ModalContent, ModalFooter } from '@/shared/components/ui/Modal';
import { isContactCompatibleWithProvider } from '@/shared/utils/channelCompatibility';
import { useChannelsDropdown } from '@/modules/channels/hooks/useChannelsDropdown';
import { TemplateSelectWithPreview } from '../../templates/components/TemplateSelectWithPreview';
import { useCreateConversation } from '../hooks';
import { Icon } from '@/shared/icons'
/** Capabilities exclusivas de canais de email — usadas para excluir do dropdown de atendimento */
const EMAIL_CAPABILITIES: ProviderCapability[] = [
  ProviderCapability.SEND_EMAIL,
  ProviderCapability.RECEIVE_EMAIL,
];

/** Providers que não suportam atendimento manual (widget é iniciado pelo visitante, não pelo atendente) */
const EXCLUDED_PROVIDERS: string[] = [
  ProviderId.WEBSITE_CHAT,
  ProviderId.WEBSITE_WIDGET,
];

interface ContactForDialog {
  id: string;
  name: string;
  identifiers?: Partial<ContactIdentifiers>;
}

interface StartConversationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contact: ContactForDialog;
}

export const StartConversationDialog: React.FC<StartConversationDialogProps> = ({
  open,
  onOpenChange,
  contact,
}) => {
  const navigate = useNavigate();
  const [selectedChannelId, setSelectedChannelId] = useState<string>('');
  const [templateValue, setTemplateValue] = useState<{ templateId: string; variables: Record<string, string> }>({ templateId: '', variables: {} });
  const [templateValid, setTemplateValid] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Buscar canais disponíveis
  const { data: channels, isLoading: loadingChannels } = useChannelsDropdown();

  // Verificar se o canal selecionado requer template (tem capability TEMPLATE_MANAGEMENT)
  const selectedChannelRequiresTemplate = useMemo(() => {
    if (!selectedChannelId || !channels) return false;

    const channel = channels.find(ch => ch.id === selectedChannelId);
    if (!channel || !channel.capabilities) return false;

    return channel.capabilities.includes(ProviderCapability.TEMPLATE_MANAGEMENT);
  }, [selectedChannelId, channels]);

  // Hook para criar conversa (já invalida queries automaticamente)
  const createConversationMutation = useCreateConversation();

  // Filtrar canais: ativos + sem email + sem widget + compatíveis com identifiers do contato
  const activeChannels = (channels || []).filter(
    channel => channel.status === 'active' &&
      !channel.capabilities?.some(cap => EMAIL_CAPABILITIES.includes(cap)) &&
      !EXCLUDED_PROVIDERS.includes(channel.providerId || '') &&
      isContactCompatibleWithProvider(channel.providerId, contact.identifiers)
  );

  // Pré-selecionar canal quando houver apenas um disponível
  useEffect(() => {
    if (activeChannels.length === 1 && !selectedChannelId) {
      setSelectedChannelId(activeChannels[0].id);
    }
  }, [activeChannels, selectedChannelId]);

  const isSubmitting = createConversationMutation.isPending;

  // Handler para mudança de canal - limpar template quando canal mudar
  const handleChannelChange = (channelId: string) => {
    setSelectedChannelId(channelId);
    setTemplateValue({ templateId: '', variables: {} }); // Reset template quando canal mudar
    setTemplateValid(false); // Reset validity when channel changes
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!selectedChannelId) {
      setError('Selecione um canal para iniciar o atendimento');
      return;
    }

    // Validar template se canal requer
    if (selectedChannelRequiresTemplate && (!templateValue.templateId || !templateValid)) {
      setError('Selecione um template e preencha todas as variáveis obrigatórias');
      return;
    }

    // Criar conversa usando mutation (invalida queries automaticamente)
    createConversationMutation.mutate(
      {
        contactId: contact.id,  // ✅ Apenas ID - contact data populated via aggregation
        channelId: selectedChannelId,
        source: 'manual',
        subject: `Atendimento - ${contact.name}`,
        priority: 'normal',
        ...(selectedChannelRequiresTemplate ? { templateId: templateValue.templateId, templateVariables: templateValue.variables } : {}),
      },
      {
        onSuccess: (conversation) => {
          // ✅ Queries já foram invalidadas pelo hook
          // Navegar para conversa criada
          navigate(`/chat/${conversation.id}`);
          onOpenChange(false);
        },
        onError: (err: any) => {
          console.error('Erro ao iniciar atendimento:', err);
          setError(err.response?.data?.message || err.message || 'Erro ao iniciar atendimento');
        }
      }
    );
  };

  // Modal shared (F71/F72): portal pro body + z-[60] + ESC + tema claro fixo.
  return (
    <Modal
      isOpen={open}
      onClose={() => !isSubmitting && onOpenChange(false)}
      maxWidth="md"
      closeOnEscape={!isSubmitting}
    >
      <ModalHeader
        title="Iniciar Atendimento"
        onClose={isSubmitting ? undefined : () => onOpenChange(false)}
      >
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center flex-none order-first"
          style={{
            backgroundColor: 'rgba(139, 92, 246, 0.1)',
            color: '#8b5cf6',
          }}
        >
          <Icon name="message-square" className="w-5 h-5" />
        </div>
      </ModalHeader>

      {/* Content */}
      <form onSubmit={handleSubmit}>
        <ModalContent>
          <div className="space-y-4">
            {/* Contato (readonly) */}
            <div>
              <label
                className="block text-sm font-medium mb-2"
                style={{ color: '#1e293b' }}
              >
                Contato
              </label>
              <Input
                type="text"
                value={contact.name}
                readOnly
                className="cursor-not-allowed"
                style={{
                  backgroundColor: '#f1f5f9',
                  borderColor: '#e5e7eb',
                  color: '#64748b',
                }}
              />
            </div>

            {/* Info: só canais compatíveis com o contato aparecem */}
            <div
              className="rounded-lg px-3 py-2 text-sm"
              style={{ backgroundColor: '#eff6ff', color: '#1d4ed8' }}
            >
              Só aparecem canais compatíveis com os identificadores deste contato.
            </div>

            {/* Seleção de Canal */}
            <div>
              <label
                className="block text-sm font-medium mb-2"
                style={{ color: '#1e293b' }}
              >
                Canal de Atendimento <span className="text-red-500">*</span>
              </label>
              {loadingChannels ? (
                <div className="flex items-center justify-center py-8">
                  <Icon name="loader" className="w-6 h-6 animate-spin" style={{ color: '#8b5cf6' }} />
                  <span
                    className="ml-2 text-sm"
                    style={{ color: '#64748b' }}
                  >
                    Carregando canais...
                  </span>
                </div>
              ) : activeChannels.length === 0 ? (
                <div
                  className="text-center py-4 text-sm"
                  style={{ color: '#64748b' }}
                >
                  Nenhum canal ativo disponível
                </div>
              ) : (
                <Select
                  value={selectedChannelId}
                  onChange={(val) => handleChannelChange(val)}
                  placeholder="Selecione um canal"
                  options={activeChannels.map((channel) => ({
                    value: channel.id,
                    label: `${channel.name} (${channel.identifier})`
                  }))}
                  disabled={isSubmitting}
                  required
                />
              )}
            </div>

            {/* Seleção de Template (Condicional - somente se canal requer) */}
            {selectedChannelRequiresTemplate && (
              <div data-testid="template-section">
                <label
                  className="block text-sm font-medium mb-2"
                  style={{ color: '#1e293b' }}
                >
                  Template <span className="text-red-500">*</span>
                </label>
                <TemplateSelectWithPreview
                  channelId={selectedChannelId}
                  value={templateValue}
                  onChange={setTemplateValue}
                  disabled={isSubmitting}
                  onValidityChange={setTemplateValid}
                />
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                <svg
                  className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}
          </div>
        </ModalContent>

        <ModalFooter>
          <Button
            type="button"
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
            data-testid="start-conversation-cancel"
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="primary"
            icon={isSubmitting ? undefined : 'message-square'}
            loading={isSubmitting}
            disabled={isSubmitting || activeChannels.length === 0 || !selectedChannelId}
            data-testid="start-conversation-submit"
          >
            {isSubmitting ? 'Iniciando...' : 'Iniciar Atendimento'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};
